import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { WO_TYPE_COLORS, priorityColors } from '../utils/constants';
import { playClick } from '../utils/sounds';
import { FiClipboard, FiAlertTriangle, FiClock, FiActivity, FiInbox, FiSearch, FiDollarSign, FiChevronDown, FiChevronRight, FiPackage, FiCheck, FiX, FiEye, FiArrowLeft } from 'react-icons/fi';
import HelpTip from '../components/HelpTip';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtDMY(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const due = new Date(dateStr);
  const now = new Date();
  due.setHours(0, 0, 0, 0);
  now.setHours(0, 0, 0, 0);
  return Math.ceil((due - now) / 86400000);
}

const fmtH = v => { const s = Math.round(parseFloat(v) * 4) / 4; const h = Math.floor(s); const m = Math.round((s - h) * 60); return m > 0 ? `${h}h${m}m` : `${h}h`; };

export default function MyWork() {
  const { authFetch, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Read-only view of a team member's my-work. When set, every data call
  // scopes to that user and personal sections (payslips) are hidden.
  const viewUserId = (() => {
    const v = parseInt(searchParams.get('user'));
    return v && v !== user?.id ? v : null;
  })();
  const [viewUserName, setViewUserName] = useState('');
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [showCompleted, setShowCompleted] = useState(false);
  const todayRef = useRef(null);
  const hasScrolled = useRef(false);

  // Awaiting parts state
  const [awaitingParts, setAwaitingParts] = useState([]);

  // Payslip state
  const [payslips, setPayslips] = useState([]);
  const [showPayslips, setShowPayslips] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);

  const toLocalDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  // Compute fresh week bounds on every call so polling/refetch after a day
  // rollover always uses the user's current local week (not stale mount-time values).
  const getWeekBounds = () => {
    const n = new Date();
    const dayOfWeek = n.getDay(); // 0=Sun, 1=Mon, ...
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const mon = new Date(n);
    mon.setDate(n.getDate() + mondayOffset);
    mon.setHours(0, 0, 0, 0);
    const sun = new Date(mon);
    sun.setDate(mon.getDate() + 6);
    return { today: toLocalDate(n), weekStart: toLocalDate(mon), weekEnd: toLocalDate(sun) };
  };

  // Tick state forces re-render when the local day changes so derived buckets refresh.
  const [dayTick, setDayTick] = useState(() => toLocalDate(new Date()));
  useEffect(() => {
    const id = setInterval(() => {
      const t = toLocalDate(new Date());
      setDayTick(prev => (prev !== t ? t : prev));
    }, 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const { today, weekStart, weekEnd } = getWeekBounds();

  const fetchMyWork = (completed) => {
    const { weekStart: ws, weekEnd: we } = getWeekBounds();
    const params = new URLSearchParams({ week_start: ws, week_end: we });
    if (completed) params.set('show_completed', '1');
    if (viewUserId) params.set('user_id', String(viewUserId));
    const url = `/api/issues/my-work?${params.toString()}`;
    return authFetch(url)
      .then(r => r.json())
      .then(res => { const data = res && res.data ? res.data : (Array.isArray(res) ? res : []); return data; });
  };

  const loadPayslips = () => {
    // Payslips are always personal to the signed-in user — never fetched in
    // read-only team-member view.
    if (viewUserId) { setPayslips([]); return; }
    authFetch('/api/payslips').then(r => r.ok ? r.json() : []).then(d => setPayslips(Array.isArray(d) ? d : [])).catch(() => {});
  };

  const loadAwaitingParts = () => {
    const url = viewUserId ? `/api/issues/my-work/awaiting-parts?user_id=${viewUserId}` : '/api/issues/my-work/awaiting-parts';
    authFetch(url).then(r => r.ok ? r.json() : []).then(d => setAwaitingParts(Array.isArray(d) ? d : [])).catch(() => {});
  };

  // Resolve the viewed user's name once (for the banner) when in read-only mode.
  useEffect(() => {
    if (!viewUserId) { setViewUserName(''); return; }
    authFetch(`/api/users/${viewUserId}`).then(r => r.ok ? r.json() : null)
      .then(u => setViewUserName(u?.name || `User #${viewUserId}`))
      .catch(() => setViewUserName(`User #${viewUserId}`));
  }, [viewUserId, authFetch]);

  useEffect(() => {
    setLoading(true);
    fetchMyWork(showCompleted).then(data => { setIssues(data); setLoading(false); }).catch(() => setLoading(false));
    loadPayslips();
    loadAwaitingParts();
  }, [showCompleted, dayTick, viewUserId]);

  // Auto-scroll to today's first work order on initial load
  useEffect(() => {
    if (!loading && issues.length > 0 && !hasScrolled.current && todayRef.current) {
      hasScrolled.current = true;
      setTimeout(() => todayRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
    }
  }, [loading, issues]);

  // Live polling every 15s
  useEffect(() => {
    const interval = setInterval(() => {
      fetchMyWork(showCompleted).then(data => {
        setIssues(prev => JSON.stringify(prev) !== JSON.stringify(data) ? data : prev);
      }).catch(() => {});
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  const applySearch = (list) => {
    let filtered = list;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(i => (i.wo_number || '').toLowerCase().includes(q) || (i.name || '').toLowerCase().includes(q) || (i.asset_name || '').toLowerCase().includes(q) || (i.first_operation_title || '').toLowerCase().includes(q));
    }
    if (typeFilter !== 'all') filtered = filtered.filter(i => i.type === typeFilter);
    if (priorityFilter !== 'all') filtered = filtered.filter(i => i.priority === priorityFilter);
    return filtered;
  };

  const filtered = applySearch(issues);

  // Use operation planned date if available, otherwise WO due date
  const effectiveDate = (i) => (i.op_planned_date || i.due_date || '').slice(0, 10);

  // Overdue = planned date has passed (before today) and not final confirmed
  const allOverdue = filtered.filter(i => effectiveDate(i) && effectiveDate(i) < today && !i.op_final_confirmed);
  const overduePrevWeeks = allOverdue.filter(i => effectiveDate(i) < weekStart)
    .sort((a, b) => effectiveDate(a).localeCompare(effectiveDate(b)));
  const overdueThisWeek = allOverdue.filter(i => effectiveDate(i) >= weekStart)
    .sort((a, b) => effectiveDate(a).localeCompare(effectiveDate(b)));

  // This week (Monday–Sunday), excluding overdue
  const dueThisWeek = filtered.filter(i => effectiveDate(i) && !(effectiveDate(i) < today && !i.op_final_confirmed) && effectiveDate(i) >= weekStart && effectiveDate(i) <= weekEnd)
    .sort((a, b) => effectiveDate(a).localeCompare(effectiveDate(b)));

  // Unscheduled (no date at all)
  const unscheduled = filtered.filter(i => !effectiveDate(i));

  const totalActive = filtered.length;

  if (loading) {
    return (
      <div>
        <div style={{ marginBottom: 24 }}>
          <div className="skeleton" style={{ width: 140, height: 28, marginBottom: 8 }} />
          <div className="skeleton" style={{ width: 220, height: 14 }} />
        </div>
        <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
          {[...Array(3)].map((_, i) => (
            <div key={i} style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 16, border: '1px solid var(--border)', flex: 1 }}>
              <div className="skeleton" style={{ width: 40, height: 28, marginBottom: 6 }} />
              <div className="skeleton" style={{ width: 60, height: 12 }} />
            </div>
          ))}
        </div>
        {[...Array(5)].map((_, i) => (
          <div key={i} style={{
            background: 'var(--bg-card)', borderRadius: 10, padding: 16,
            marginBottom: 8, border: '1px solid var(--border)'
          }}>
            <div className="skeleton" style={{ width: '60%', height: 16, marginBottom: 8 }} />
            <div className="skeleton" style={{ width: '35%', height: 12 }} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      {viewUserId && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', marginBottom: 16,
          borderRadius: 10, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)',
          color: '#b45309', fontSize: 13, fontWeight: 600
        }}>
          <FiEye size={16} />
          <span style={{ flex: 1 }}>
            Read-only view of <strong>{viewUserName || `user #${viewUserId}`}</strong>'s work. Clicking a work order opens it for inspection only.
          </span>
          <button onClick={() => navigate('/my-work')}
            style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid rgba(245,158,11,0.5)', background: 'transparent', color: '#b45309', cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}>
            <FiArrowLeft size={12} /> Back to my work
          </button>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--text-primary)' }}>
            {viewUserId ? `${viewUserName || 'Team member'} — Work` : 'My Work'}
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: 'var(--text-secondary)' }}>
            Working week: {formatDate(weekStart)} – {formatDate(weekEnd)} · {totalActive} active work order{totalActive !== 1 ? 's' : ''}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative' }}>
            <FiSearch size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input
              type="text" placeholder="Search WO, name, asset..."
              value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              style={{ padding: '8px 12px 8px 30px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, outline: 'none', background: 'var(--bg-input)', color: 'var(--text-primary)', minWidth: 180 }}
            />
          </div>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
            style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 12, background: 'var(--bg-input)', color: 'var(--text-primary)', cursor: 'pointer' }}>
            <option value="all">All Types</option>
            <option value="PM01">PM01</option><option value="PM02">PM02</option><option value="PM03">PM03</option>
          </select>
          <button onClick={() => setShowCompleted(prev => !prev)}
            style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${showCompleted ? '#2563eb' : 'var(--border)'}`, background: showCompleted ? 'rgba(37,99,235,0.08)' : 'var(--bg-input)', color: showCompleted ? '#2563eb' : 'var(--text-muted)', cursor: 'pointer', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', transition: 'all 0.15s' }}>
            {showCompleted ? 'Hide Completed' : 'Show Completed'}
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
        <SummaryCard label="Overdue" count={overduePrevWeeks.length + overdueThisWeek.length} color="#ef4444" icon={FiAlertTriangle} />
        <SummaryCard label="This Week" count={dueThisWeek.length} color="#f59e0b" icon={FiClock} />
        <SummaryCard label="Total" count={totalActive} color="#0ea5e9" icon={FiActivity} />
      </div>

      {/* Overdue from previous weeks */}
      {overduePrevWeeks.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: '#dc2626', margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiAlertTriangle size={15} /> Overdue from Previous Weeks
            <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)' }}>({overduePrevWeeks.length})</span>
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {overduePrevWeeks.map((issue, idx) => <WORow key={`${issue.id}-${issue.operation_id || idx}`} issue={issue} idx={idx} today={today} navigate={navigate} />)}
          </div>
        </div>
      )}

      {/* Overdue from this week */}
      {overdueThisWeek.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: '#ef4444', margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiAlertTriangle size={15} /> Overdue This Week
            <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)' }}>({overdueThisWeek.length})</span>
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {overdueThisWeek.map((issue, idx) => <WORow key={`${issue.id}-${issue.operation_id || idx}`} issue={issue} idx={idx} today={today} navigate={navigate} />)}
          </div>
        </div>
      )}

      {/* Due This Week section */}
      {dueThisWeek.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: '#f59e0b', margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiClock size={15} /> Due This Week
            <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)' }}>({dueThisWeek.length})</span>
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(() => { let foundToday = false; return dueThisWeek.map((issue, idx) => {
              const isToday = effectiveDate(issue) === today && !foundToday;
              if (isToday) foundToday = true;
              return <div key={`${issue.id}-${issue.operation_id || idx}`} ref={isToday ? todayRef : undefined}><WORow issue={issue} idx={idx} today={today} navigate={navigate} /></div>;
            }); })()}
          </div>
        </div>
      )}

      {/* Unscheduled section */}
      {unscheduled.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-muted)', margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiInbox size={15} /> No Due Date
            <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)' }}>({unscheduled.length})</span>
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {unscheduled.map((issue, idx) => <WORow key={`${issue.id}-${issue.operation_id || idx}`} issue={issue} idx={idx} today={today} navigate={navigate} />)}
          </div>
        </div>
      )}

      {totalActive === 0 && (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
          <FiClipboard size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
          <div style={{ fontSize: 15 }}>No work orders with unconfirmed operations</div>
        </div>
      )}

      {/* Awaiting Parts/Service Section */}
      {awaitingParts.length > 0 && (
        <div style={{ marginTop: 32, borderTop: '1px solid var(--border)', paddingTop: 24, marginBottom: 24 }}>
          <h2 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 700, color: '#d97706', display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiPackage size={16} /> Awaiting Parts / Service
            <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)' }}>({awaitingParts.length})</span>
            <HelpTip text="Jobs assigned to you with pending materials. Each item shows ordered/delivered/installed status with dates. Click a job to open the work order." />
          </h2>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>Jobs assigned to you with pending materials — regardless of scheduled date</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {awaitingParts.map((job, idx) => {
              const materials = Array.isArray(job.materials) ? job.materials : [];
              const getMatChecks = (m) => {
                if (m.material_type === 'labour') return [
                  { label: 'Contacted', done: !!m.contacted, date: m.contacted_date },
                  { label: 'Labour Complete', done: !!m.labour_complete, date: m.labour_complete_date },
                  { label: 'Paid', done: !!m.paid, date: m.paid_date },
                ];
                if (m.material_type === 'service') return [
                  { label: 'Invoice Received', done: !!m.ordered, date: m.ordered_date },
                  { label: 'Approved', done: !!m.delivered, date: m.delivered_date },
                  { label: 'Paid', done: !!m.installed, date: m.installed_date },
                ];
                return [
                  { label: 'Ordered', done: !!m.ordered, date: m.ordered_date },
                  { label: 'Delivered', done: !!m.delivered, date: m.delivered_date },
                  { label: 'Installed', done: !!m.installed, date: m.installed_date },
                ];
              };
              return (
                <motion.div key={job.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.03 }}
                  style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border)', borderLeft: '3px solid #f59e0b', overflow: 'hidden' }}>
                  <div onClick={() => { playClick(); navigate(`/work-orders/${job.wo_number}`); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', cursor: 'pointer', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 120, flexShrink: 0 }}>
                      <span style={{ padding: '1px 5px', borderRadius: 3, fontSize: 9, fontWeight: 700, color: '#fff', background: WO_TYPE_COLORS[job.type] || '#6b7280' }}>{job.type}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'monospace' }}>{job.wo_number}</span>
                    </div>
                    <div style={{ flex: 1, minWidth: 140 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {job.asset_name && <span style={{ fontWeight: 600 }}>{job.asset_name}</span>}
                        {job.due_date && <span>{job.asset_name ? ' · ' : ''}Due {formatDate(job.due_date)}</span>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, flexWrap: 'nowrap' }}>
                      <span style={{ padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: `${priorityColors[job.priority] || '#6b7280'}15`, color: priorityColors[job.priority] || '#6b7280' }}>{job.priority}</span>
                      {job.pending_materials_count > 0 && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: 'rgba(245,158,11,0.12)', color: '#d97706', border: '1px solid rgba(245,158,11,0.3)', whiteSpace: 'nowrap' }}>
                          <FiPackage size={10} /> {job.pending_materials_count}
                        </span>
                      )}
                      {materials.length > 0 && materials.length - job.pending_materials_count > 0 && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: 'rgba(22,163,74,0.1)', color: '#16a34a', border: '1px solid rgba(22,163,74,0.25)', whiteSpace: 'nowrap' }}>
                          <FiCheck size={10} /> {materials.length - job.pending_materials_count}
                        </span>
                      )}
                    </div>
                  </div>
                  {materials.length > 0 && (
                    <div style={{ borderTop: '1px solid var(--border)', padding: '8px 16px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {materials.map(m => {
                        const checks = getMatChecks(m);
                        const allDone = checks.every(c => c.done);
                        const typeLabel = m.material_type === 'labour' ? 'Labour' : m.material_type === 'service' ? 'Service' : 'Part';
                        const typeColor = m.material_type === 'labour' ? '#7c3aed' : m.material_type === 'service' ? '#0284c7' : '#374151';
                        return (
                          <div key={m.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap', padding: '7px 8px', borderRadius: 7, background: allDone ? 'rgba(22,163,74,0.05)' : 'rgba(245,158,11,0.05)', border: `1px solid ${allDone ? 'rgba(22,163,74,0.2)' : 'rgba(245,158,11,0.2)'}` }}>
                            <div style={{ flex: '0 0 auto', minWidth: 120 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
                                <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 4, background: `${typeColor}18`, color: typeColor }}>{typeLabel}</span>
                                {allDone && <FiCheck size={11} color="#16a34a" />}
                              </div>
                              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>{m.part_name}</div>
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, flex: 1 }}>
                              {checks.map(c => (
                                <span key={c.label} style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '2px 7px', borderRadius: 5, fontSize: 11, fontWeight: 600, background: c.done ? 'rgba(22,163,74,0.1)' : 'rgba(107,114,128,0.08)', color: c.done ? '#16a34a' : '#9ca3af', border: `1px solid ${c.done ? 'rgba(22,163,74,0.25)' : 'rgba(107,114,128,0.15)'}` }}>
                                  {c.done ? <FiCheck size={10} /> : <FiX size={10} />}
                                  {c.label}
                                  {c.done && c.date && <span style={{ fontWeight: 500 }}>{' '}{fmtDMY(c.date)}</span>}
                                </span>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      )}

      {/* Payslips Section */}
      {payslips.length > 0 && (
        <div style={{ marginTop: 32, borderTop: '1px solid var(--border)', paddingTop: 24 }}>
          <div onClick={() => { playClick(); setShowPayslips(!showPayslips); }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', marginBottom: showPayslips ? 16 : 0 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <FiDollarSign size={18} /> My Payslips
              <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)' }}>({payslips.length})</span>
            </h2>
            {showPayslips ? <FiChevronDown size={18} color="var(--text-muted)" /> : <FiChevronRight size={18} color="var(--text-muted)" />}
          </div>
          {showPayslips && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {payslips.map(ps => (
                <div key={ps.id} onClick={() => { playClick(); setSelectedPayslip(selectedPayslip?.id === ps.id ? null : ps); }}
                  style={{ padding: '14px 16px', borderRadius: 10, background: 'var(--bg-card)', border: `1px solid ${selectedPayslip?.id === ps.id ? 'var(--accent)' : 'var(--border)'}`, cursor: 'pointer' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                        Pay Period: {formatDate(ps.period_start)} – {formatDate(ps.period_end)}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                        Paid: {formatDate(ps.pay_date)} · {fmtH(ps.hours_worked)} worked{ps.leave_hours > 0 ? ` · ${fmtH(ps.leave_hours)} leave` : ''}
                      </div>
                    </div>
                    <span style={{ fontSize: 16, fontWeight: 700, color: '#16a34a', fontFamily: 'monospace' }}>
                      ${parseFloat(ps.gross_pay).toFixed(2)}
                    </span>
                  </div>
                  {selectedPayslip?.id === ps.id && (
                    <div style={{ marginTop: 12, padding: 14, borderRadius: 8, background: 'var(--bg-input)', border: '1px solid var(--border)', fontSize: 12 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <div><span style={{ color: 'var(--text-muted)' }}>Company:</span> <strong>{ps.company_name}</strong></div>
                        <div><span style={{ color: 'var(--text-muted)' }}>Employee:</span> <strong>{ps.employee_name}</strong></div>
                        <div><span style={{ color: 'var(--text-muted)' }}>Employee ID:</span> <strong>{ps.employee_id || '-'}</strong></div>
                        <div><span style={{ color: 'var(--text-muted)' }}>Role:</span> <strong>{ps.role || '-'}</strong></div>
                        <div><span style={{ color: 'var(--text-muted)' }}>Hourly Rate:</span> <strong>${parseFloat(ps.base_rate).toFixed(2)}/hr</strong></div>
                        <div><span style={{ color: 'var(--text-muted)' }}>Hours Worked:</span> <strong>{fmtH(ps.hours_worked)}</strong></div>
                        {ps.leave_hours > 0 && <div><span style={{ color: 'var(--text-muted)' }}>Leave Hours:</span> <strong>{fmtH(ps.leave_hours)}</strong></div>}
                        {ps.leave_type_breakdown && (() => { try { const b = JSON.parse(ps.leave_type_breakdown); return Object.entries(b).map(([t, h]) => <div key={t}><span style={{ color: 'var(--text-muted)' }}>{t === 'annual' ? 'Annual' : t === 'sick' ? 'Sick' : t === 'long_service' ? 'Long Service' : t}:</span> <strong>{fmtH(h)}</strong></div>); } catch { return null; } })()}
                        <div style={{ gridColumn: '1/-1', borderTop: '1px solid var(--border)', paddingTop: 8, marginTop: 4 }}>
                          <span style={{ color: 'var(--text-muted)' }}>Gross Pay:</span> <strong style={{ fontSize: 16, color: '#16a34a' }}>${parseFloat(ps.gross_pay).toFixed(2)}</strong>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function WORow({ issue, idx, today, navigate }) {
  const displayDate = issue.op_planned_date || issue.due_date;
  const isOverdue = displayDate && displayDate.slice(0, 10) < today;
  const days = daysUntil(displayDate);
  const dueLabel = days === null ? '' : days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? 'Due today' : days === 1 ? 'Due tomorrow' : `Due in ${days}d`;
  const isPM02 = issue.type === 'PM02';

  return (
    <div
      onClick={() => { playClick(); navigate(`/work-orders/${issue.wo_number || issue.id}`); }}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
        background: isPM02 ? 'rgba(139,92,246,0.04)' : 'var(--bg-card)',
        borderRadius: 8, cursor: 'pointer',
        border: `1px solid ${isOverdue ? 'rgba(239,68,68,0.3)' : isPM02 ? 'rgba(139,92,246,0.25)' : 'var(--border)'}`,
        borderLeft: isOverdue ? '3px solid #ef4444' : isPM02 ? '3px solid #8b5cf6' : '3px solid transparent',
        touchAction: 'manipulation', flexWrap: 'wrap', transition: 'all 0.15s'
      }}
      onMouseEnter={e => { e.currentTarget.style.background = isPM02 ? 'rgba(139,92,246,0.10)' : 'rgba(37,99,235,0.06)'; e.currentTarget.style.borderColor = isPM02 ? 'rgba(139,92,246,0.4)' : 'rgba(37,99,235,0.3)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = isPM02 ? 'rgba(139,92,246,0.04)' : 'var(--bg-card)'; e.currentTarget.style.borderColor = isOverdue ? 'rgba(239,68,68,0.3)' : isPM02 ? 'rgba(139,92,246,0.25)' : 'var(--border)'; }}
    >
      {/* Left: WO number / operation + type */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 120, flexShrink: 0 }}>
        <span style={{
          padding: '1px 5px', borderRadius: 3, fontSize: 9, fontWeight: 700,
          color: '#fff', background: WO_TYPE_COLORS[issue.type] || '#6b7280'
        }}>{issue.type}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'monospace' }}>
          {issue.wo_number}{issue.first_operation ? `/${issue.first_operation}` : ''}
        </span>
      </div>

      {/* Center: operation name (main) + WO name + asset */}
      <div style={{ flex: 1, minWidth: 140 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{issue.first_operation_title || issue.name}</div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {issue.first_operation_title && issue.first_operation_title !== issue.name && <span style={{ color: 'var(--accent)', fontWeight: 500 }}>WO: {issue.name}</span>}
          {issue.first_operation_title && issue.first_operation_title !== issue.name && issue.asset_name && <span> · </span>}
          {issue.asset_name && <span style={{ fontWeight: 600 }}>{issue.asset_name}</span>}
          {issue.op_planned_hours > 0 && <span> · {fmtH(issue.op_planned_hours)}</span>}
        </div>
      </div>

      {/* Right: Priority + due info + overdue badge */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, flexWrap: 'nowrap' }}>
        <span style={{
          padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600,
          background: `${priorityColors[issue.priority] || '#6b7280'}15`,
          color: priorityColors[issue.priority] || '#6b7280'
        }}>{issue.priority}</span>

        {displayDate && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: 70 }}>
            <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-secondary)' }}>
              {formatDate(displayDate)}
            </span>
            <span style={{
              fontSize: 10, fontWeight: 600,
              color: isOverdue ? '#ef4444' : days === 0 ? '#f59e0b' : 'var(--text-muted)'
            }}>
              {dueLabel}
            </span>
          </div>
        )}

        <ConfirmBadge issue={issue} />
      </div>
    </div>
  );
}

const CONF_STYLES = {
  CNF: { background: '#dcfce7', color: '#16a34a', border: '1px solid #bbf7d0' },
  PCNF: { background: '#fef9c3', color: '#ca8a04', border: '1px solid #fde68a' },
  NEW: { background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' }
};

function ConfirmBadge({ issue }) {
  let label = 'NEW';
  // Per-operation status if available
  if (issue.op_final_confirmed) label = 'CNF';
  else if (issue.op_tc_count > 0) label = 'PCNF';
  else if (issue.op_count > 0 && issue.op_confirmed_count === issue.op_count) label = 'CNF';
  else if (issue.tc_count > 0) label = 'PCNF';
  const s = CONF_STYLES[label];
  return (
    <span style={{
      padding: '3px 7px', borderRadius: 5, fontSize: 10, fontWeight: 700,
      letterSpacing: '0.5px', ...s
    }}>{label}</span>
  );
}

function SummaryCard({ label, count, color, icon: Icon }) {
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      style={{
        background: 'var(--bg-card)', borderRadius: 10, padding: 16,
        border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12
      }}
    >
      <div style={{
        width: 38, height: 38, borderRadius: 10, background: `${color}12`,
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        <Icon size={18} color={color} />
      </div>
      <div>
        <div style={{ fontSize: 22, fontWeight: 700, color, lineHeight: 1 }}>{count}</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 500, marginTop: 2 }}>{label}</div>
      </div>
    </motion.div>
  );
}

